import React from "react";
import { Chip, ChipProps } from "@mui/material";
import { colors } from "@/theme/colors";

export type OrderStatus =
  | "pending"
  | "confirmed"
  | "inProgress"
  | "ready"
  | "inDelivery"
  | "delivered"
  | "cancelled";

export interface StatusBadgeProps extends Omit<ChipProps, "label" | "color"> {
  status: OrderStatus;
  showDot?: boolean;
}

const statusConfig: Record<
  OrderStatus,
  { label: string; bg: string; text: string; border: string; dot: string }
> = {
  pending: {
    label: "En Attente",
    bg: colors.status.pending.bg,
    text: colors.status.pending.text,
    border: colors.status.pending.border,
    dot: colors.status.pending.dot,
  },
  confirmed: {
    label: "Confirmé",
    bg: "#dbeafe", // blue-100
    text: "#1e40af", // blue-800
    border: "#93c5fd", // blue-300
    dot: "#3b82f6", // blue-500
  },
  inProgress: {
    label: "En Cours",
    bg: colors.status.inProgress.bg,
    text: colors.status.inProgress.text,
    border: colors.status.inProgress.border,
    dot: colors.status.inProgress.dot,
  },
  ready: {
    label: "Prêt",
    bg: "#e9d5ff", // purple-100
    text: "#6b21a8", // purple-800
    border: "#c084fc", // purple-300
    dot: "#9333ea", // purple-500
  },
  inDelivery: {
    label: "En Livraison",
    bg: "#dbeafe", // blue-100
    text: "#1e40af", // blue-800
    border: "#93c5fd", // blue-300
    dot: "#3b82f6", // blue-500
  },
  delivered: {
    label: "Livré",
    bg: colors.status.delivered.bg,
    text: colors.status.delivered.text,
    border: colors.status.delivered.border,
    dot: colors.status.delivered.dot,
  },
  cancelled: {
    label: "Annulé",
    bg: colors.status.cancelled.bg,
    text: colors.status.cancelled.text,
    border: colors.status.cancelled.border,
    dot: colors.status.cancelled.dot,
  },
};

/**
 * Composant StatusBadge pour afficher le statut d'une commande
 *
 * @example
 * ```tsx
 * <StatusBadge status="pending" />
 * <StatusBadge status="delivered" showDot />
 * ```
 */
export const StatusBadge: React.FC<StatusBadgeProps> = ({
  status,
  showDot = false,
  sx,
  ...props
}) => {
  const config = statusConfig[status];

  return (
    <Chip
      label={
        <span style={{ display: "flex", alignItems: "center", gap: "6px" }}>
          {showDot && (
            <span
              style={{
                width: "6px",
                height: "6px",
                borderRadius: "50%",
                backgroundColor: config.dot,
                display: "inline-block",
              }}
            />
          )}
          {config.label}
        </span>
      }
      sx={{
        bgcolor: config.bg,
        color: config.text,
        border: `1px solid ${config.border}`,
        borderRadius: "9999px", // rounded-full
        fontSize: "12px",
        fontWeight: 500,
        height: "auto",
        py: 0.5,
        "& .MuiChip-label": {
          px: 1.25,
        },
        ...sx,
      }}
      {...props}
    />
  );
};
